/**
 * Who the employer on a scraped offer actually is.
 *
 * `scraped_jobs.company_name` is whatever the source wrote, and the same
 * employer arrives spelled several ways across a week of scraping — "Acciona",
 * "ACCIONA S.A.", "Acciona, S.A". Counting open vacancies per employer means
 * deciding when two spellings are one company, and that decision has to survive
 * a page reload, because the outreach status is stored against it.
 *
 * So this lives in `config/`, not in the backend: the admin panel computes the
 * key when it groups the queue, and the backend imports the same function. Two
 * implementations that drift would orphan every prospect row whose key was
 * written by the other one.
 */

/**
 * Company names that intentionally hide the employer.
 *
 * These must never be enriched, relinked or prospected — there is no real brand
 * behind them, and grouping by name would fuse every confidential posting in the
 * database into one enormous fake "company" sitting at the top of the list.
 */
const ANONYMOUS_NAMES = new Set([
    "confidencial",
    "confidential",
    "anonimo",
    "anónimo",
    "empresa confidencial",
    "n/a",
    "na",
    "-",
]);

export function isAnonymousCompanyName(name: string | null | undefined): boolean {
    if (!name) return true;
    return ANONYMOUS_NAMES.has(name.trim().toLowerCase());
}

/**
 * The employer's name as a screen should print it.
 *
 * Scraped offers carry whatever the board wrote, and boards write the absence
 * of a name in a dozen ways: "confidential" in lower-case English on a Spanish
 * posting, "CONFIDENCIAL", "n/a", "-". All of them mean the same thing and
 * none of them is a company, so all of them say so in one voice.
 */
export function employerDisplayName(name: string | null | undefined): string {
    const text = (name ?? "").trim();
    return isAnonymousCompanyName(text) ? "Empresa confidencial" : text;
}

/**
 * Trailing tokens that are a legal form rather than part of the name.
 *
 * Only ever stripped from the *end*, and only after punctuation is gone, which
 * is what makes the single letters safe: "S.A. de C.V." has become "s a de c v"
 * by then, and no company is named for a letter it ends on. `grupo` and `group`
 * are deliberately absent — they are part of the name often enough ("Grupo
 * Bimbo") that stripping them would merge unrelated employers.
 */
const LEGAL_SUFFIXES = new Set([
    // The single letters are the pieces the legal forms break into once the
    // dots are gone: "S.R.L." arrives as `s r l`, and missing `r` alone leaves
    // a key of `…-soporte-s-r`. Cheap to add now and not later — the key is
    // what a prospect's outreach status is stored against, so changing this
    // function after rows exist orphans them.
    "s", "a", "l", "u", "c", "v", "r", "de", "y",
    "sa", "sl", "slu", "sau", "sas", "sac", "srl", "spa", "sapi",
    "ltda", "lda", "ltd", "limited", "llc", "inc", "incorporated",
    "corp", "corporation", "co", "company",
    "gmbh", "bv", "nv", "plc", "ag", "ab", "oy", "as", "aps",
]);

/**
 * The stable identity of an employer, derived from how the source spelled it.
 *
 * Accent-folded, punctuation-free, legal form removed, hyphen-joined:
 * "ACCIONA, S.A." and "Acciona" both give `acciona`.
 *
 * Deliberately not a similarity match. Two names either normalise to the same
 * string or they do not, so the grouping is reproducible from the name alone —
 * no threshold to tune, and no row that changes group because a different row
 * arrived. It under-merges (a typo stays separate) rather than over-merging,
 * which is the right way round: a duplicated prospect is a nuisance, a fused one
 * hides an employer behind another employer's outreach status.
 *
 * Never returns an empty string for a non-empty name — a name made entirely of
 * suffix tokens keeps its unstripped form rather than collapsing to "", which
 * would group it with every other such name.
 */
export function employerKey(name: string | null | undefined): string {
    const cleaned = (name ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

    if (!cleaned) return "";

    const tokens = cleaned.split(" ");

    // Nothing but a legal form, e.g. a `company_name` of "S.A.". Strip nothing:
    // peeling from the end would leave "s" for both "S.A." and "S.L.", which
    // merges two employers that share no name at all.
    if (tokens.every(t => LEGAL_SUFFIXES.has(t))) return tokens.join("-");

    const kept = [...tokens];
    while (kept.length > 1 && LEGAL_SUFFIXES.has(kept[kept.length - 1])) {
        kept.pop();
    }
    return kept.join("-");
}

/**
 * The spelling to show for an employer, given every spelling its offers used.
 *
 * The most frequent one — the way this employer usually appears is the way a
 * reader will recognise it. Ties break on the shortest, which drops the legal
 * form: "Iberdrola" and "Iberdrola, S.A." key the same and appear once each, and
 * the first is the name anyone would search for.
 *
 * Note that the variants here differ only in punctuation and legal form, since
 * anything else lands on a different key: "Acciona Energía" is `acciona-energia`
 * and stays its own prospect.
 */
export function preferredEmployerName(names: readonly string[]): string {
    const counts = new Map<string, number>();
    for (const n of names) {
        const trimmed = (n ?? "").trim();
        if (trimmed) counts.set(trimmed, (counts.get(trimmed) ?? 0) + 1);
    }
    let best = "";
    let bestCount = 0;
    for (const [name, count] of counts) {
        if (count > bestCount || (count === bestCount && name.length < best.length)) {
            best = name;
            bestCount = count;
        }
    }
    return best;
}

/**
 * The employer hiding in an email address, for one who is in no table at all.
 *
 * The prospect list was derived from the scrape queue, so an employer who was
 * never scraped could not be recorded — which is exactly backwards, because the
 * best lead this business gets is the company that writes to us first. Their own
 * domain is the evidence, and it is offered to a person to confirm rather than
 * acted on: `ThreadLinkPicker` shows the name it derives and creates nothing
 * until somebody presses the button.
 *
 * Free mailbox providers are refused outright. "Gmail" is not a company, and a
 * list that will happily create it as a prospect is a list that poisons itself
 * the first time a candidate writes in from a personal address. There is no
 * inference available for those and the honest answer is null — somebody types
 * the employer's name by hand, which is the correct amount of work for a case
 * where the address says nothing.
 */
const FREE_MAIL_DOMAINS = new Set([
    "gmail.com", "googlemail.com", "hotmail.com", "hotmail.es", "outlook.com", "outlook.es",
    "live.com", "msn.com", "yahoo.com", "yahoo.es", "yahoo.com.mx", "icloud.com", "me.com",
    "mac.com", "aol.com", "proton.me", "protonmail.com", "gmx.com", "gmx.es", "zoho.com",
    "mail.com", "yandex.com", "hotmail.com.mx", "terra.com", "terra.com.br", "uol.com.br",
    "bol.com.br", "telefonica.net", "movistar.es"
]);

/**
 * Whether an address at this domain says nothing about who employs its owner.
 *
 * Exported for the company claim (`company_claim.ts`), which asks the same
 * question with more at stake: there, a free mailbox that counted as evidence
 * would let anybody with a Gmail account take over whichever company the brand
 * enricher once stamped `gmail.com` on.
 */
export function isFreeMailDomain(domain: string | null | undefined): boolean {
    return FREE_MAIL_DOMAINS.has((domain ?? "").trim().toLowerCase());
}

/**
 * Suffixes under which the *next* label is the registered name.
 *
 * Only the ones this market actually produces — Spain and Latin America, plus
 * the UK, which turns up on international employers. Not the public suffix list:
 * that is a megabyte and a dependency for a question this narrow.
 */
const SECOND_LEVEL_SUFFIXES = new Set([
    "com.mx", "org.mx", "net.mx", "gob.mx",
    "com.br", "net.br", "org.br",
    "com.ar", "com.co", "com.pe", "com.ec", "com.uy", "com.ve", "com.bo",
    "com.py", "com.gt", "com.sv", "com.hn", "com.ni", "com.pa", "com.do",
    "co.cr", "co.uk", "org.uk", "com.es"
]);

/**
 * Whether a domain belongs to a university.
 *
 * `lmonroyc@ucentral.edu.co`, `kv.gutierrez@uniandes.edu.co`,
 * `ajsabogalr@unal.edu.co` — three of the applications in the mailbox on
 * 2026-09-05, every one a student or a graduate writing from the address
 * their university gave them. A university is an employer somewhere, but a
 * person writing from one to this mailbox is a candidate, and offering to
 * create the prospect "Edu" (the label before `.co`, which the suffix list
 * did not know) was the picker's worst first impression. The `edu` and `ac`
 * labels are how the world's academic registries spell it.
 */
export function isAcademicDomain(domain: string | null | undefined): boolean {
    const labels = (domain ?? "").trim().toLowerCase().split(".");
    return labels.includes("edu") || labels.includes("ac");
}

export function employerFromAddress(
    address: string | null | undefined
): { name: string; key: string; website: string } | null {
    const domain = (address ?? "").split("@")[1]?.trim().toLowerCase().replace(/[>,;\s]+$/, "");
    if (!domain || !domain.includes(".") || FREE_MAIL_DOMAINS.has(domain)) return null;
    // A university address says who taught them, not who employs them.
    if (isAcademicDomain(domain)) return null;

    // The registrable label rather than the whole host: mail from
    // "rrhh.acciona.com" is Acciona and the subdomain is the department, so
    // taking the first label would file the same employer under "Rrhh".
    //
    // Which label that is depends on the suffix, and Latin America is full of
    // two-part ones — "acme.com.mx" registers `acme`, not `com`. Rather than
    // carry the public suffix list, this knows the handful that appear in this
    // market; anything else is treated as a single-label TLD, which is the
    // right answer for .com/.es/.io and every other case seen here.
    const parts = domain.split(".").filter(Boolean);
    const suffixIsTwoPart = parts.length > 2
        && SECOND_LEVEL_SUFFIXES.has(parts.slice(-2).join("."));
    const label = (suffixIsTwoPart ? parts[parts.length - 3] : parts[parts.length - 2]) ?? "";
    const name = label
        .replace(/[-_]+/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim();
    if (!name || isAnonymousCompanyName(name)) return null;

    const key = employerKey(name);
    if (!key) return null;
    return { name, key, website: `https://${domain}` };
}
